import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { asyncStandupPath, dailyStandupCostPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

const FAQS = [
  {
    question: 'How does this wasted money meeting calculator work?',
    answer:
      'Multiply attendees by meeting duration, weekly frequency, a fully-loaded hourly salary, and 48 working weeks. Optional attendance and overrun factors model no-shows and meetings that run long. The result is the annual amount of money wasted on that meeting: the standup tax.',
  },
  {
    question: 'What is the standup tax?',
    answer:
      'The standup tax is the fully-loaded salary cost of a recurring status meeting. A 15-minute daily standup for six engineers at $100/hour is about $36,000 a year before overrun. Git Digest replaces that ritual with commit digests in Slack or email.',
  },
  {
    question: 'How do I see the amount of money wasted on a meeting?',
    answer:
      'You do not need a custom script. Enter teams, attendees, minutes, and a fully-loaded hourly salary. The calculator returns the annual amount of money wasted on that meeting.',
  },
  {
    question: 'Can this price wasted meeting time for a whole company?',
    answer:
      'Yes. Set the number of teams, the people in the meeting, and how often you meet. The annual figure is the company cost of that recurring meeting, the standup tax, before you add planning or the weekly sync.',
  },
  {
    question: 'Is this a Harvard meeting calculator?',
    answer:
      'People search for a Harvard meeting calculator when they want a number for time wasted in meetings. This page does not republish a Harvard study, and it does not invent a statistic. Enter salary, attendees, and duration to see the money and time your company spends.',
  },
  {
    question: 'Can Git Digest replace our standup?',
    answer:
      'Git Digest turns GitHub activity into team commit summaries delivered to Slack and email, so engineering leads get an async standup from shipped code instead of another calendar invite.',
  },
  {
    question: 'Is Git Digest a repo ingest tool like Gitingest?',
    answer:
      'No. Gitingest turns a repository into text for LLMs. Git Digest is a team product: daily or weekly GitHub commit digests for Slack and email that replace status meetings.',
  },
] as const

export function MeetingWasteSeo() {
  return (
    <div className="mx-auto mt-16 max-w-4xl space-y-12 md:mt-24">
      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">The standup tax, in plain numbers</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Recurring status meetings look cheap because they are on the calendar. This{' '}
            <strong className="text-foreground">wasted money meeting calculator</strong> prices them like payroll:
            fully-loaded salary × attendees × duration × how often the company meets.
          </p>
          <p>
            We call that the <strong className="text-foreground">standup tax</strong>. A 15-minute daily standup for six
            engineers at $100/hour is about $36,000 a year before anyone overruns the Zoom. Add the usual 1.2× overrun
            and you have funded a mid-level engineer to ship instead of narrate. The same math scales from one team to
            every team in the meeting, which is the company cost of that wasted time.
          </p>
          <p>
            Git Digest replaces that ritual with{' '}
            <Link href="/" className={linkClass}>
              team GitHub and Slack commit digests
            </Link>
            . The source of truth is what landed in git, not what people remember in a circle. Git Digest is not
            Gitingest, and it is not a tool that turns a repository into text for an LLM.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Company cost of time wasted in meetings</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            People look for a Harvard meeting calculator when they want a number for time wasted in meetings. This page
            does not republish a Harvard study, and it does not invent a percentage. It uses your salary, your
            attendees, and your duration, so the wasted money is yours.
          </p>
          <p>
            You do not need a script. Set the number of teams when several groups share the meeting. One team shows the
            squad cost. Many teams show the company cost of the same ritual: the annual amount of money wasted on that
            meeting.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">How the calculator works</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Annual cost = teams × attendees × meetings per week × (minutes ÷ 60) × 48 working weeks × hourly salary ×
            attendance × overrun. Vacation is already baked in as 48 weeks, not 52.
          </p>
          <p>
            Use a fully-loaded hourly salary (compensation plus benefits and overhead), not the sticker wage. For a
            $150k engineer that is often $75 to $120/hour. Tune attendees and duration for standups, planning, or the
            weekly sync that never dies.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">From the number to a smaller meeting</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            The{' '}
            <Link href={dailyStandupCostPath()} className={linkClass}>
              daily standup cost
            </Link>{' '}
            table prices a 15-minute ritual by salary and team size. The{' '}
            <Link href={asyncStandupPath()} className={linkClass}>
              async standup
            </Link>{' '}
            playbook is the next step: cadence, timezones, and blockers, with GitHub commit digests in Slack or email
            instead of another status meeting.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Wasted money calculator FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Common questions about wasted meeting money, company meeting time, and replacing status meetings with
            digests.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {FAQS.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Replace the standup with GitHub digests</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub and get daily or weekly team commit summaries in Slack and email. Same alignment, none of the
          standup tax.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href="/sign_in?utm_source=meeting-waste&utm_campaign=calculator">
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
